const designCollaboration = {
  "id": "design-collaboration",
  "slug": "design-collaboration",
  "category": "Foundations",
  "navTitle": "Modernizing Design Collaboration",
  "title": "Modernizing Design Collaboration",
  "introduction": [
    "Following the Euronet acquisition, XE’s design team expanded to 17 people working across five time zones. The existing combination of Sketch files, version-control tools, prototyping platforms, and collaboration software made it harder to work together and keep projects aligned.",
    "I led the move to Figma and reviewed the wider toolset so the team could collaborate more easily, reduce duplication, and support a growing distributed practice."
  ],
  "challenge": [
    {
      "title": "A distributed team",
      "paragraphs": [
        "Designers were working across several countries and time zones. Sharing files, reviewing work, and knowing which version was current became increasingly difficult."
      ]
    },
    {
      "title": "Too many overlapping tools",
      "paragraphs": [
        "The team relied on several products for design, prototyping, file sharing, handoff, workshops, and testing. Some performed similar functions, creating extra cost and unnecessary handoffs."
      ]
    },
    {
      "title": "An established workflow",
      "paragraphs": [
        "The team was already delivering active projects in Sketch and related tools. Any transition needed to protect ongoing work and avoid creating a long period of disruption."
      ]
    },
    {
      "title": "Wider cross-functional needs",
      "paragraphs": [
        "The new setup also had to work for developers, product managers, and other partners who reviewed designs or relied on specifications."
      ]
    }
  ],
  "process": [
    {
      "title": "Understanding where the workflow was breaking down",
      "paragraphs": [
        "I reviewed how the team was using its existing tools and where distributed work was creating the most friction."
      ],
      "bullets": [
        "Spoke with designers about file management, reviews, handoffs, and collaboration across time zones.",
        "Mapped how work moved between Sketch, version-control tools, prototyping software, Zeplin, Miro, and UserTesting.",
        "Identified duplicated capabilities, licensing gaps, and points where information was being lost.",
        "Compared Figma and Adobe XD against the team’s collaboration, scalability, and integration needs.",
        "Included product and engineering requirements in the evaluation."
      ],
      "closing": "This gave us a clearer basis for choosing tools instead of replacing software one product at a time."
    },
    {
      "title": "Moving active work into Figma",
      "paragraphs": [
        "I planned the transition around active projects so the team could adopt Figma without stopping delivery."
      ],
      "bullets": [
        "Migrated shared assets, libraries, and selected project files from Sketch.",
        "Created guidance for file structure, naming, permissions, and component use.",
        "Delivered training sessions and practical support for designers with different levels of experience.",
        "Allowed teams to complete some active work in existing tools where an immediate move would create unnecessary risk.",
        "Used early projects to identify issues and improve the migration approach."
      ],
      "closing": "The transition happened in stages, with enough flexibility to keep current work moving."
    },
    {
      "title": "Simplifying the wider toolset",
      "paragraphs": [
        "I consolidated tools around the parts of the workflow they supported best and removed overlap where it no longer added value."
      ],
      "bullets": [
        "Established Figma as the shared environment for interface design, prototyping, and real-time review.",
        "Retained tools such as Miro and UserTesting where they supported distinct needs.",
        "Reviewed whether Zeplin was still required as development teams became more comfortable working directly from Figma.",
        "Worked with product managers and developers to align reviews and handoffs with the new workflow.",
        "Reviewed licences and vendor agreements to reduce unnecessary cost."
      ],
      "closing": "The updated setup gave the team a simpler workflow while still supporting research, workshops, and development collaboration."
    }
  ],
  "outcomes": [
    {
      "title": "Easier collaboration across time zones",
      "description": "Designers could review and contribute to the same files without managing separate versions or waiting for files to be handed over."
    },
    {
      "title": "Less workflow friction",
      "description": "A more focused toolset reduced duplicated steps and made it easier for the team to know where work and feedback belonged."
    },
    {
      "title": "Faster reviews and handoffs",
      "description": "Product managers and developers had clearer access to current designs, prototypes, components, and specifications."
    },
    {
      "title": "Lower software costs",
      "description": "Consolidating overlapping tools and reviewing licences reduced ongoing software spending."
    },
    {
      "title": "A stronger foundation for the distributed team",
      "description": "Shared files, libraries, and working practices made it easier for 17 designers across five time zones to operate as one team."
    }
  ],
  "reflection": [
    "The move to Figma solved a practical collaboration problem, but the software alone did not improve the workflow. The team also needed clear file structures, shared working practices, training, and agreement on where different types of work should happen.",
    "Planning the transition around active projects helped us avoid unnecessary disruption. It also gave the team time to adapt the workflow based on actual use instead of trying to define every rule before anyone had worked in the new environment."
  ]
};

export default designCollaboration;

